import { Component, OnDestroy } from '@angular/core'
import { Store } from '@ngrx/store'
import {
    selectHierarchicalSelectionNodeItem,
    selectSavedHierarchicalSelectionNodeItems,
    selectHierarchicalSelectionIsRoot,
    defaultHierarchicalSelectionNodeItem,
    selectDisplayedSelectionsOnScatterPlot,
} from '../../../state/selection/selection.selectors'
import { selectionActions } from '../../../state/selection/selection.actions'
import { selectSavedAndCurrentHierarchicalSelectionsAsLayers } from '../../../state/selection/selection.selectors'
import { Dialog } from '@angular/cdk/dialog'
import { SaveSelectionAsDialogComponent } from '../../save-selection-as-dialog/save-selection-as-dialog.component'
import { Subject, takeUntil, take } from 'rxjs'
import { GenericConfirmationDialogComponent } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component'
import { GenericConfirmationDialogModel } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.model'
import { selectIsSharedCase } from '../../../state/case/case.selectors'
import { SelectionTreeItem } from '../../../state/selection/selection.state'
import { ToasterService } from '../../../shared/services/toaster.service'

@Component({
    selector: 'app-saved-hierarchical-selection-nodes',
    templateUrl: './saved-hierarchical-selection-nodes.component.html',
    styleUrls: ['./saved-hierarchical-selection-nodes.component.scss'],
})
export class SavedHierarchicalSelectionNodesComponent implements OnDestroy {
    readonly currentHierarchicalSelectionNodeItem$ = this.store.select(
        selectHierarchicalSelectionNodeItem
    )
    readonly savedHierarchicalSelectionNodeItems$ = this.store.select(
        selectSavedHierarchicalSelectionNodeItems
    )
    readonly currentHierarchicalSelectionIsRoot$ = this.store.select(
        selectHierarchicalSelectionIsRoot
    )
    readonly allHierarchicalSelectionNodeItems$ = this.store.select(
        selectSavedAndCurrentHierarchicalSelectionsAsLayers(this.store)
    )
    readonly isSharedCase$ = this.store.select(selectIsSharedCase)
    readonly displayedSelections$ = this.store.select(selectDisplayedSelectionsOnScatterPlot)

    readonly defaultHierarchicalSelectionNodeItem: SelectionTreeItem =
        defaultHierarchicalSelectionNodeItem

    private readonly destroy$ = new Subject<void>()

    constructor(
        private readonly store: Store,
        private readonly dialog: Dialog,
        private readonly toasterService: ToasterService
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    addHierarchicalSelectionNode(): void {
        let savedHierarchicalSelections: string[] = []
        this.savedHierarchicalSelectionNodeItems$.pipe(take(1)).subscribe(savedItems => {
            savedItems.map(s => s.name && savedHierarchicalSelections.push(s.name))
        })

        const dialogRef = this.dialog.open<string>(SaveSelectionAsDialogComponent, {
            autoFocus: 'first-tabbable',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(name => {
            if (!name) return
            if (savedHierarchicalSelections.includes(name || '')) {
                this.toasterService.error('Selection name already exists', 'Error saving selection')
                return
            }
            this.store.dispatch(selectionActions.saveNewHierarchicalSelectionNode({ name }))
            this.displayedSelections$.pipe(take(1)).subscribe(currentSelections => {
                this.store.dispatch(
                    selectionActions.setVisibleSelectionsOnScatterPlot({
                        selectionNames: [...currentSelections.map(s => s.name!), name],
                    })
                )
            })
        })
    }

    setSelection(selectionTreeItem: SelectionTreeItem): void {
        this.store.dispatch(
            selectionActions.setHierarchicalSelectionNode({ name: selectionTreeItem.name })
        )

        this.displayedSelections$.pipe(take(1)).subscribe(currentSelections => {
            this.store.dispatch(
                selectionActions.setVisibleSelectionsOnScatterPlot({
                    selectionNames: [
                        ...currentSelections.map(s => s.name!),
                        selectionTreeItem.name!,
                    ],
                })
            )
        })
    }

    removeHierarchicalSelectionNode(selectionTreeItem: SelectionTreeItem): void {
        if (!selectionTreeItem.name) return
        const data: GenericConfirmationDialogModel = {
            title: 'Are you sure you want to delete this hierarchy node?',
            item: selectionTreeItem.name,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(selectionActions.deleteHierarchicalSelectionNode())
        })
    }

    removeHierarchicalSelectionNodeRecursively(selectionTreeItem: SelectionTreeItem): void {
        if (!selectionTreeItem.name) return
        const data: GenericConfirmationDialogModel = {
            title: 'Are you sure you want to delete this hierarchy node?',
            item: `${selectionTreeItem.name} (Recursively)`,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(selectionActions.deleteHierarchicalSelectionNodeRecursively())
        })
    }

    compareSelectionTreeItems(
        selectionTreeItem1: SelectionTreeItem | null,
        selectionTreeItem2: SelectionTreeItem | null
    ): boolean {
        if (!selectionTreeItem1 || !selectionTreeItem2) {
            return selectionTreeItem1 === selectionTreeItem2
        }
        return selectionTreeItem1.name === selectionTreeItem2.name
    }
}
